import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { TOS_VERSION } from "@/pages/Terms";
import { toast } from "sonner";

/**
 * TosGate — modal that appears once when a logged-in user hasn't yet accepted
 * the current Terms of Use version. The user cannot dismiss it without either:
 *   – tapping "I agree" (recorded server-side), or
 *   – logging out.
 * Apple UGC requirement: existing users must actively re-accept when the ToS
 * changes; we do not silently assume acceptance.
 */
export default function TosGate() {
    const { user, logout } = useAuth();
    const [needsAccept, setNeedsAccept] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!user) { setNeedsAccept(false); return; }
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get("/legal/tos-status");
                if (!cancelled && data && data.up_to_date === false) {
                    setNeedsAccept(true);
                }
            } catch { /* silent */ }
        })();
        return () => { cancelled = true; };
    }, [user]);

    const accept = async () => {
        setSubmitting(true);
        try {
            await api.post("/legal/accept-tos", { tos_version: TOS_VERSION });
            setNeedsAccept(false);
            toast.success("Thanks — you're all set.");
        } catch (err) {
            toast.error("Couldn't record your acceptance. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <AnimatePresence>
            {needsAccept && (
                <motion.div
                    key="tos-backdrop"
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="fixed inset-0 z-[80] bg-foreground/60 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
                    data-testid="tos-gate-backdrop"
                    style={{ paddingBottom: "max(1rem, env(safe-area-inset-bottom))" }}
                >
                    <motion.div
                        initial={{ opacity: 0, y: 30, scale: 0.96 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 20 }}
                        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                        data-testid="tos-gate"
                        className="w-full max-w-md bg-white rounded-3xl shadow-2xl overflow-hidden"
                    >
                        <div className="p-5 sm:p-6 text-white"
                             style={{ background: "linear-gradient(135deg, #f4a261 0%, #ee8f6d 45%, #dd6b4d 100%)" }}>
                            <div className="flex items-center gap-3">
                                <div className="w-12 h-12 rounded-2xl bg-white/95 text-primary flex items-center justify-center shadow-lg shrink-0">
                                    <ShieldCheck className="w-6 h-6" strokeWidth={2.2} />
                                </div>
                                <div className="min-w-0">
                                    <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-white/80">Please accept</div>
                                    <h2 className="font-display text-xl sm:text-2xl font-bold leading-tight">Our Terms of Use have been updated</h2>
                                </div>
                            </div>
                        </div>
                        <div className="p-5 sm:p-6 space-y-4">
                            <p className="text-sm text-foreground/85 leading-relaxed">
                                To keep the community safe we've updated our rules on content, harassment and misleading listings.
                                Please review and accept the new{" "}
                                <Link to="/terms" target="_blank" rel="noopener noreferrer"
                                      className="text-primary hover:underline font-medium"
                                      data-testid="tos-gate-terms-link">
                                    Terms of Use
                                </Link>{" "}
                                (version {TOS_VERSION}) to continue using ErasmusSwap.
                            </p>
                            <div className="flex flex-col sm:flex-row gap-2">
                                <Button
                                    variant="outline"
                                    onClick={logout}
                                    data-testid="tos-gate-logout"
                                    className="flex-1 h-11 rounded-full"
                                >
                                    Log out
                                </Button>
                                <Button
                                    onClick={accept}
                                    disabled={submitting}
                                    data-testid="tos-gate-accept"
                                    className="flex-1 h-11 rounded-full bg-primary hover:bg-primary/90 font-semibold"
                                >
                                    {submitting ? "Saving…" : "I agree — continue"}
                                </Button>
                            </div>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
